// @flow
import { type ActionType, handleActions } from 'redux-actions';
import { Map, Record } from 'immutable';
import { ModerationState, Product, createModels } from 'models';
import {
  addItem,
  addItems,
  commitObservationsUpdate,
  updateObservations,
  updateSiteStatus,
  receiveSiteStatusUpdate,
  commitSiteStatusUpdate,
  requestObservationsUpdate,
  receiveObservationsUpdate,
  requestAddResponse,
  receiveAddResponse,
  requestDeleteResponse,
  receiveDeleteResponse,
  requestEditResponse,
  receiveEditResponse,
  updateUgc,
  removeUgc,
} from './coreDataActions';
import { type AsyncValueType } from 'lib/async-value';
import { AsyncValues } from '../lib/async-value';

type AsyncBoolean = AsyncValueType<boolean>;
type CoreDataShape = {
  ugcs: Map<UgcId, Ugc>,
  subjects: Map<Id, Subject>,
  moderationStates: Map<UgcId, ModerationState>,
  observationRequestStatus: Map<UgcId, RequestStatus>,
  subjectIdByUgcId: Map<string, string>,
  pendingSiteStatus: Map<UgcId, AsyncBoolean>,
};

const defaultCoreDataRecordShape: CoreDataShape = {
  ugcs: Map(),
  subjects: Map(),
  moderationStates: Map(),
  observationRequestStatus: Map(),
  responseRequestStatus: Map(),
  deleteResponseRequestStatus: Map(),
  subjectIdByUgcId: Map(),
  pendingSiteStatus: Map(),
};

export type CoreDatum = {
  ugc: Ugc,
  moderationState: ModerationState,
  subject: Product,
  observationRequestStatus: ?RequestStatus,
  responseRequestStatus: ?RequestStatus,
  deleteResponseRequestStatus: ?RequestStatus,
  pendingSiteStatus: ?AsyncBoolean,
};

export class CoreDataState extends Record(defaultCoreDataRecordShape) {
  static getTheThings(state: CoreDataState, ugcId: ?UgcId = -1): ?CoreDatum {
    const ugc = state.getIn(['ugcs', ugcId]);
    const moderationState = state.getIn(['moderationStates', ugcId]);
    const subjectId = state.getIn(['subjectIdByUgcId', ugcId]);
    const subject = subjectId
      ? state.getIn(['subjects', subjectId])
      : undefined;
    const observationRequestStatus = state.getIn([
      'observationRequestStatus',
      ugcId,
    ]);
    const responseRequestStatus = state.getIn(['responseRequestStatus', ugcId]);
    const deleteResponseRequestStatus = state.getIn([
      'deleteResponseRequestStatus',
      ugcId,
    ]);
    const pendingSiteStatus = state.getIn(['pendingSiteStatus', ugcId]);

    if (!ugc || !moderationState || !subject) return undefined;

    return {
      ugc,
      moderationState,
      subject,
      observationRequestStatus,
      responseRequestStatus,
      deleteResponseRequestStatus,
      pendingSiteStatus,
    };
  }

  static removeUgc(state: CoreDataState, ugcId: UgcId) {
    const remove = collection => collection.remove(ugcId);
    const {
      ugcs,
      moderationStates,
      observationRequestStatus,
      subjectIdByUgcId,
      deleteResponseRequestStatus,
      responseRequestStatus,
      pendingSiteStatus,
    } = state;

    return state.merge({
      ugcs: remove(ugcs),
      moderationStates: remove(moderationStates),
      observationRequestStatus: remove(observationRequestStatus),
      subjectIdByUgcId: remove(subjectIdByUgcId),
      deleteResponseRequestStatus: remove(deleteResponseRequestStatus),
      responseRequestStatus: remove(responseRequestStatus),
      pendingSiteStatus: remove(pendingSiteStatus),
    });
  }
}

const defaultCoreDataState = new CoreDataState();
type State = typeof defaultCoreDataState;

export function processItem(
  payload: $PropertyType<ActionType<typeof addItem>, 'payload'>
) {
  const { ugc, subject, moderationState, ugcId, subjectId } = createModels(
    payload.data,
    payload.observationsMap
  );

  return [
    [['ugcs', ugcId], ugc],
    [['subjects', subjectId], subject],
    [['moderationStates', ugcId], moderationState],
    [['subjectIdByUgcId', ugcId], subjectId],
  ];
}

export default handleActions(
  {
    [addItem.toString()]: (
      state: State,
      action: ActionType<typeof addItem>
    ) => {
      const setInArgumentList = processItem(action.payload);

      return state.withMutations(state => {
        setInArgumentList.forEach(args => state.setIn(...args));
      });
    },

    [addItems.toString()]: (
      state: State,
      action: ActionType<typeof addItems>
    ) => {
      const {
        observationsMap,
        data: { items },
      } = action.payload;
      const setInArgumentList = items.reduce(
        (acc, item) => acc.concat(processItem({ data: item, observationsMap })),
        []
      );

      return state.withMutations(state => {
        setInArgumentList.forEach(args => state.setIn(...args));
      });
    },

    [updateUgc.toString()]: (
      state: State,
      action: ActionType<typeof updateUgc>
    ) => {
      const { payload: propsOrUpdater, meta: ugcId } = action;
      const updateFn =
        typeof propsOrUpdater === 'function'
          ? propsOrUpdater
          : ugc => ugc.merge(propsOrUpdater);

      return state.updateIn(['ugcs', ugcId], updateFn);
    },

    [removeUgc.toString()]: (
      state: State,
      action: ActionType<typeof removeUgc>
    ) => {
      const ugcId = action.payload;

      return CoreDataState.removeUgc(state, ugcId);
    },

    [updateObservations.toString()]: (state, action) =>
      state.setIn(
        ['moderationStates', action.payload.getUGCId()],
        action.payload
      ),

    [commitObservationsUpdate.toString()]: (
      state: State,
      action: ActionType<typeof commitObservationsUpdate>
    ) =>
      state.withMutations(state => {
        const ugcId = action.payload.getUGCId();
        state.setIn(['moderationStates', ugcId], action.payload);
        state.deleteIn(['observationRequestStatus', ugcId]);

        const published = ModerationState.isSavedPublished(
          action.payload,
          action.meta
        );

        state.setIn(['ugcs', ugcId, 'published'], published);
      }),

    [updateSiteStatus.toString()]: (
      state: State,
      action: ActionType<typeof updateSiteStatus>
    ) => {
      const { moderationState } = action.payload;
      const ugcId = moderationState.getUGCId();

      return state.setIn(['pendingSiteStatus', ugcId], AsyncValues.Pending);
    },

    [receiveSiteStatusUpdate.toString()]: (
      state: State,
      action: ActionType<typeof receiveSiteStatusUpdate>
    ) => {
      const { error, payload, meta: ugcId } = action;

      return error === true
        ? state.setIn(['pendingSiteStatus', ugcId], payload)
        : state.removeIn(['pendingSiteStatus', ugcId]);
    },

    [commitSiteStatusUpdate.toString()]: (
      state: State,
      action: ActionType<typeof commitSiteStatusUpdate>
    ) => {
      const ugcId = action.meta;

      return state.removeIn(['pendingSiteStatus', ugcId]);
    },

    [requestObservationsUpdate.toString()]: (
      state: State,
      action: ActionType<typeof requestObservationsUpdate>
    ) =>
      state.setIn(
        ['observationRequestStatus', action.payload.getUGCId()],
        'pending'
      ),

    [receiveObservationsUpdate.toString()]: {
      next(
        state: CoreDataState,
        action: ActionType<typeof receiveObservationsUpdate>
      ) {
        return state.setIn(
          ['observationRequestStatus', action.payload],
          'complete'
        );
      },
      throw(
        state: CoreDataState,
        action: ActionType<typeof receiveObservationsUpdate>
      ) {
        return state.setIn(
          ['observationRequestStatus', action.meta],
          action.payload
        );
      },
    },

    [requestAddResponse.toString()]: (
      state: CoreDataState,
      action: ActionType<typeof requestAddResponse>
    ) => {
      return state.setIn(['responseRequestStatus', action.meta], 'pending');
    },
    [receiveAddResponse.toString()]: {
      next(
        state: CoreDataState,
        action: ActionType<typeof receiveAddResponse>
      ) {
        const { meta: parentUgcId, payload: childUgc } = action;

        return state
          .updateIn(['ugcs', parentUgcId, 'ugc_related_content'], v =>
            v.push(childUgc)
          )
          .deleteIn(['responseRequestStatus', parentUgcId]);
      },
      throw(
        state: CoreDataState,
        action: ActionType<typeof receiveAddResponse>
      ) {
        return state.setIn(
          ['responseRequestStatus', action.meta],
          action.payload
        );
      },
    },

    //
    // Delete Response
    //
    [requestDeleteResponse.toString()]: (
      state: CoreDataState,
      action: ActionType<typeof requestDeleteResponse>
    ) => {
      return state.setIn(
        ['deleteResponseRequestStatus', action.meta],
        'pending'
      );
    },

    [receiveDeleteResponse.toString()]: (
      state: CoreDataState,
      action: ActionType<typeof receiveDeleteResponse>
    ) => {
      const { meta: parentUgcId, payload } = action;

      if (action.error === true) {
        return state.setIn(
          ['deleteResponseRequestStatus', parentUgcId],
          payload
        );
      }

      // simulate deletion by removing the merchant response
      return state
        .updateIn(['ugcs', parentUgcId, 'ugc_related_content'], v => {
          const index = v.findIndex(ugc => ugc.ugctype === 'MerchantResponse');

          return index > -1 ? v.remove(index) : v;
        })
        .deleteIn(['deleteResponseRequestStatus', parentUgcId]);
    },

    //
    // Edit
    //
    [requestEditResponse.toString()]: (
      state: CoreDataState,
      action: ActionType<typeof requestEditResponse>
    ) => {
      return state.setIn(['responseRequestStatus', action.meta], 'pending');
    },
    [receiveEditResponse.toString()]: {
      next(
        state: CoreDataState,
        action: ActionType<typeof receiveEditResponse>
      ) {
        const { meta: parentUgcId, payload: updatedChildUgc } = action;

        return state
          .updateIn(['ugcs', parentUgcId, 'ugc_related_content'], v =>
            v.map(
              ugc =>
                ugc.ugc_id === updatedChildUgc.ugc_id ? updatedChildUgc : ugc
            )
          )
          .deleteIn(['responseRequestStatus', parentUgcId]);
      },
      throw(
        state: CoreDataState,
        action: ActionType<typeof receiveAddResponse>
      ) {
        return state.setIn(
          ['responseRequestStatus', action.meta],
          action.payload
        );
      },
    },
  },
  defaultCoreDataState
);



// WEBPACK FOOTER //
// ./src/ducks/coreDataReducer.js